import { useEffect, useState } from "react"
import { X, Users, Activity, TrendingUp, Check, Loader2 } from "lucide-react"
import api from "../request" // Adjust the import based on your API setup

export default function HotStepperRequestModal({ isOpen, onClose }) {
  const [hotSteppers, setHotSteppers] = useState([])
  const [loading, setLoading] = useState(true)
  const [requestStatus, setRequestStatus] = useState({}) // key: hotStepperId, value: 'pending' | 'sent'

  useEffect(() => {
    if (!isOpen) return

    const fetchHotSteppers = async () => {
      setLoading(true)
      try {
        const res = await api.get("/hot-steppers/available") // Backend should return requests where hot_stepper_id = currentUser.id

        setHotSteppers(res.data || [])
      } catch (err) {
        console.error("Failed to fetch hot steppers:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchHotSteppers()
  }, [isOpen])

  const sendHotStepperRequest = async (hotStepperId) => {
    setRequestStatus((prev) => ({ ...prev, [hotStepperId]: "pending" }))

    try {
      const res = await api.post("/hot-stepper/requests", {
        hot_stepper_id: hotStepperId,
      })

      console.log("Hot Stepper Request Sent:", res.data)
      setRequestStatus((prev) => ({ ...prev, [hotStepperId]: "sent" }))
    } catch (error) {
      console.error("Error sending request:", error)
      setRequestStatus((prev) => ({ ...prev, [hotStepperId]: undefined }))
      alert("Failed to send request.")
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl relative max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-primary text-white p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-all duration-200"
          >
            <X size={20} />
          </button>
          <div className="flex items-center gap-3 pr-12">
            <div className="p-3 bg-white bg-opacity-20 rounded-xl">
              <Users size={28} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-1">Request a Hot Stepper</h2>
              <p className="text-blue-100 text-sm">Connect with top performers to boost your group's motivation</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Loading State */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="p-4 bg-primary bg-opacity-10 rounded-2xl mb-4">
                <Loader2 size={32} className="text-primary animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Finding Hot Steppers</h3>
              <p className="text-gray-500 text-center">Searching for available top performers...</p>
            </div>
          ) : hotSteppers.length === 0 ? (
            <div className="text-center py-20">
              <div className="p-4 bg-gray-100 rounded-2xl inline-flex items-center justify-center mb-4">
                <Users size={32} className="text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">No Hot Steppers Available</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                All hot steppers are currently busy. Check back later for available top performers to invite to your
                group.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Available Hot Steppers</h3>
                <p className="text-gray-600 text-sm">Choose from our top performers to join your group</p>
              </div>

              <div className="grid gap-4">
                {hotSteppers.map((user) => (
                  <div
                    key={user.id}
                    className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-primary hover:border-opacity-30 transition-all duration-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <img
                            src={user.avatar || "/placeholder.svg?height=56&width=56"}
                            alt={user.name}
                            className="w-14 h-14 rounded-full object-cover border-2 border-gray-100"
                          />
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                        </div>

                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-gray-900 mb-1">{user.name}</h4>

                          <div className="flex items-center gap-2 mb-3">
                            <div className="p-1 bg-primary bg-opacity-10 rounded">
                              <Users size={12} className="text-primary" />
                            </div>
                            <span className="text-sm text-gray-600 font-medium">{user.group_name}</span>
                          </div>

                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                              <div className="p-1 bg-blue-100 rounded">
                                <Activity size={12} className="text-blue-600" />
                              </div>
                              <div>
                                <span className="text-xs text-gray-500 block">Today</span>
                                <span className="text-sm font-bold text-gray-900">
                                  {user.daily_steps?.toLocaleString() || "0"} steps
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <div className="p-1 bg-green-100 rounded">
                                <TrendingUp size={12} className="text-green-600" />
                              </div>
                              <div>
                                <span className="text-xs text-gray-500 block">This Week</span>
                                <span className="text-sm font-bold text-gray-900">
                                  {user.weekly_steps?.toLocaleString() || "0"} steps
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end">
                        <button
                          onClick={() => sendHotStepperRequest(user.id)}
                          className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-200 min-w-[140px] flex items-center justify-center gap-2 ${
                            requestStatus[user.id] === "pending"
                              ? "bg-amber-100 text-amber-800 cursor-not-allowed"
                              : requestStatus[user.id] === "sent"
                                ? "bg-green-100 text-green-800 cursor-not-allowed"
                                : "bg-primary text-white hover:bg-opacity-90 hover:shadow-lg active:scale-95"
                          }`}
                          disabled={requestStatus[user.id] === "pending" || requestStatus[user.id] === "sent"}
                        >
                          {requestStatus[user.id] === "pending" ? (
                            <>
                              <Loader2 size={16} className="animate-spin" />
                              <span>Sending...</span>
                            </>
                          ) : requestStatus[user.id] === "sent" ? (
                            <>
                              <Check size={16} />
                              <span>Request Sent</span>
                            </>
                          ) : (
                            <span>Send Request</span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>💡 Hot Steppers are top performers who can motivate your group</span>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
